/**
 * NetworkClub News Service
 * Service voor het ophalen, filteren en beheren van netwerkclub nieuws
 * Inclusief matching logica op basis van gebruiker werkgebied
 */

import { 
  collection, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  NetworkClubNews, 
  NewsFilterTab
} from '../types/networkclub';
import { UserProfile } from '../types';

// =====================================================
// DEMO DATA (voor development/testing)
// =====================================================

const DEMO_NEWS: NetworkClubNews[] = [
  {
    id: 'demo-news-1',
    clubId: 'demo-1',
    clubName: 'Amsterdam Entrepreneurs Network',
    clubLogoUrl: '',
    title: 'Nieuwjaars Netwerk Borrel 2026',
    content: 'Start het jaar goed! Kom naar onze jaarlijkse nieuwjaarsborrel met 200+ ondernemers.',
    imageUrl: '',
    linkUrl: 'https://example.com/event',
    targetScope: 'regionaal',
    targetRegions: ['Noord-Holland', 'Utrecht'],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 dagen geleden
    isActive: true,
    views: 156,
    authorId: 'demo-author-1',
    authorName: 'Jan de Vries'
  },
  {
    id: 'demo-news-2',
    clubId: 'demo-2',
    clubName: 'Rotterdam Business Club',
    clubLogoUrl: '',
    title: 'Masterclass: Schaal je bedrijf internationaal',
    content: 'Leer van ervaren ondernemers hoe je succesvol de grens over gaat.',
    imageUrl: '',
    targetScope: 'nationaal',
    targetRegions: [],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 dagen geleden
    isActive: true,
    views: 89,
    authorId: 'demo-author-2',
    authorName: 'Lisa Bakker'
  },
  {
    id: 'demo-news-3',
    clubId: 'demo-3',
    clubName: 'European Startup Alliance',
    clubLogoUrl: '',
    title: 'Cross-border Investment Summit 2026',
    content: 'Connect with investors from NL, BE, DE and UK. 500+ attendees expected.',
    imageUrl: '',
    linkUrl: 'https://example.com/summit',
    targetScope: 'internationaal',
    targetRegions: [],
    targetCountries: ['Nederland', 'België', 'Duitsland', 'Verenigd Koninkrijk'],
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 dag geleden
    isActive: true,
    views: 342,
    authorId: 'demo-author-3',
    authorName: 'European Startup Alliance'
  },
  {
    id: 'demo-news-4',
    clubId: 'demo-4',
    clubName: 'Brabant Ondernemers Netwerk',
    clubLogoUrl: '',
    title: 'Opening nieuwe locatie in Eindhoven',
    content: 'Onze nieuwe hub in de Brainport regio is nu open. Kom langs voor een rondleiding!',
    imageUrl: '',
    targetScope: 'lokaal',
    targetRegions: ['Noord-Brabant'],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 dagen geleden
    isActive: true,
    views: 67,
    authorId: 'demo-author-4',
    authorName: 'Piet Jansen'
  },
  {
    id: 'demo-news-5',
    clubId: 'demo-5',
    clubName: 'Tech Founders NL',
    clubLogoUrl: '',
    title: 'AI & Automation Meetup - Maart 2026',
    content: 'Ontdek hoe AI jouw business kan transformeren. Demo\'s van 5 tech startups.',
    imageUrl: '',
    targetScope: 'nationaal',
    targetRegions: [],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000), // 4 dagen geleden
    isActive: true,
    views: 203,
    authorId: 'demo-author-5',
    authorName: 'Tech Founders NL'
  },
  {
    id: 'demo-news-6',
    clubId: 'demo-6',
    clubName: 'Friesland Business Network',
    clubLogoUrl: '',
    title: 'Netwerkbijeenkomst Leeuwarden',
    content: 'Maandelijkse netwerkbijeenkomst in het centrum van Leeuwarden.',
    imageUrl: '',
    targetScope: 'lokaal',
    targetRegions: ['Friesland'],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000), // 6 dagen geleden
    isActive: true,
    views: 45,
    authorId: 'demo-author-6',
    authorName: 'Friesland Business Network'
  },
  {
    id: 'demo-news-7',
    clubId: 'demo-7',
    clubName: 'BeNeLux Entrepreneurs',
    clubLogoUrl: '',
    title: 'Grensoverschrijdend Ondernemen Workshop',
    content: 'Praktische tips voor ondernemen in Nederland, België en Luxemburg.',
    imageUrl: '',
    targetScope: 'internationaal',
    targetRegions: [],
    targetCountries: ['Nederland', 'België', 'Luxemburg'],
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 dagen geleden
    isActive: true,
    views: 128,
    authorId: 'demo-author-7',
    authorName: 'BeNeLux Entrepreneurs'
  },
  {
    id: 'demo-news-8',
    clubId: 'demo-8',
    clubName: 'Utrecht Startup Community',
    clubLogoUrl: '',
    title: 'Pitch Night: Zoek je co-founder?',
    content: 'Pitch je idee en vind je perfecte co-founder. 3 minuten per pitch.',
    imageUrl: '',
    targetScope: 'regionaal',
    targetRegions: ['Utrecht', 'Gelderland'],
    targetCountries: ['Nederland'],
    createdAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000), // 8 dagen geleden
    isActive: true,
    views: 178,
    authorId: 'demo-author-8',
    authorName: 'Utrecht Startup Community'
  }
];

// =====================================================
// SERVICE CLASS
// =====================================================

class NetworkClubNewsService {
  private collectionName = 'networkClubNews';

  /**
   * Haal alle actieve nieuws items op
   */
  async getAllActiveNews(): Promise<NetworkClubNews[]> {
    try {
      const newsRef = collection(db, this.collectionName);
      const q = query(
        newsRef,
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('📰 Geen nieuws in Firebase, gebruik demo data');
        return DEMO_NEWS.filter(n => n.isActive);
      }
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate?.() || undefined,
        updatedAt: doc.data().updatedAt?.toDate?.() || undefined
      } as NetworkClubNews));
      
    } catch (error) {
      console.error('Error fetching news:', error);
      // Fallback naar demo data bij error
      return DEMO_NEWS.filter(n => n.isActive);
    }
  }

  /**
   * Haal nieuws op voor een specifieke club
   */
  async getNewsByClubId(clubId: string): Promise<NetworkClubNews[]> {
    try {
      const newsRef = collection(db, this.collectionName);
      const q = query(
        newsRef,
        where('clubId', '==', clubId),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      } as NetworkClubNews));
      
    } catch (error) {
      console.error('Error fetching club news:', error);
      return DEMO_NEWS.filter(n => n.clubId === clubId && n.isActive);
    }
  }

  /**
   * Maak nieuw nieuws item aan
   */
  async createNews(news: Omit<NetworkClubNews, 'id' | 'createdAt' | 'views'>): Promise<string> {
    try {
      const newsRef = collection(db, this.collectionName);
      const docRef = await addDoc(newsRef, {
        ...news,
        createdAt: serverTimestamp(),
        views: 0
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating news:', error);
      throw error;
    }
  }

  /**
   * Update nieuws item
   */
  async updateNews(newsId: string, updates: Partial<NetworkClubNews>): Promise<void> {
    try {
      const newsRef = doc(db, this.collectionName, newsId);
      await updateDoc(newsRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating news:', error);
      throw error;
    }
  }

  /**
   * Verwijder nieuws item
   */
  async deleteNews(newsId: string): Promise<void> {
    try {
      const newsRef = doc(db, this.collectionName, newsId);
      await deleteDoc(newsRef);
    } catch (error) {
      console.error('Error deleting news:', error);
      throw error;
    }
  }

  /**
   * Verhoog view count
   */
  async incrementViews(newsId: string): Promise<void> {
    try {
      const newsRef = doc(db, this.collectionName, newsId);
      const newsDoc = await getDoc(newsRef);
      
      if (newsDoc.exists()) {
        const currentViews = newsDoc.data().views || 0;
        await updateDoc(newsRef, {
          views: currentViews + 1
        });
      }
    } catch (error) {
      console.error('Error incrementing views:', error);
    }
  }

  // =====================================================
  // FILTERING & MATCHING LOGICA
  // =====================================================

  /**
   * Bepaal welke filter tabs beschikbaar zijn voor een gebruiker
   */
  getAvailableTabs(userWorkScope: string | undefined): NewsFilterTab[] {
    switch (userWorkScope) {
      case 'internationaal':
        return ['all', 'international', 'national', 'myRegion'];
      case 'nationaal':
        return ['all', 'national', 'myRegion'];
      case 'regionaal':
      case 'lokaal':
      default:
        return ['all', 'myRegion'];
    }
  }

  /**
   * Filter nieuws op basis van geselecteerde tab
   */
  filterNewsByTab(
    news: NetworkClubNews[],
    tab: NewsFilterTab,
    userRegions: string[],
    userCountry: string = 'Nederland'
  ): NetworkClubNews[] {
    switch (tab) {
      case 'international':
        return news.filter(n => n.targetScope === 'internationaal');
        
      case 'national':
        return news.filter(n => n.targetScope === 'nationaal');
        
      case 'myRegion':
        return news.filter(n => 
          (n.targetScope === 'lokaal' || n.targetScope === 'regionaal') &&
          n.targetRegions.some(r => userRegions.includes(r))
        );
        
      case 'all':
      default:
        return this.sortByRelevance(news, userRegions);
    }
  }

  /**
   * Haal relevant nieuws op voor een specifieke gebruiker
   * Combineert scope-matching met regio-matching
   */
  getRelevantNewsForUser(
    allNews: NetworkClubNews[],
    userProfile: UserProfile
  ): NetworkClubNews[] {
    const userScope = userProfile.workScope || 'lokaal';
    const userRegions = (userProfile as any).selectedRegions || 
                        [(userProfile as any).region].filter(Boolean);
    
    return allNews.filter(news => {
      // Internationale gebruiker ziet alles
      if (userScope === 'internationaal') {
        return true;
      }
      
      // Nationale gebruiker ziet nationaal + regionale matches
      if (userScope === 'nationaal') {
        if (news.targetScope === 'nationaal') return true;
        if (news.targetScope === 'internationaal') return true;
        if (news.targetScope === 'lokaal' || news.targetScope === 'regionaal') {
          return news.targetRegions.some(r => userRegions.includes(r));
        }
      }
      
      // Lokale gebruiker ziet regionale + nationale matches
      if (userScope === 'lokaal') {
        if (news.targetScope === 'nationaal') return true;
        if (news.targetScope === 'lokaal' || news.targetScope === 'regionaal') {
          return news.targetRegions.some(r => userRegions.includes(r));
        }
      }
      
      return false;
    });
  }

  /**
   * Sorteer nieuws op relevantie voor gebruiker
   * Mijn regio eerst, dan nationaal, dan internationaal
   */
  sortByRelevance(news: NetworkClubNews[], userRegions: string[]): NetworkClubNews[] {
    return [...news].sort((a, b) => {
      const scoreA = this.getRelevanceScore(a, userRegions);
      const scoreB = this.getRelevanceScore(b, userRegions);
      
      // Eerst op relevantie, dan op datum
      if (scoreA !== scoreB) return scoreB - scoreA;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  /**
   * Bereken relevantie score voor een nieuws item
   */
  private getRelevanceScore(news: NetworkClubNews, userRegions: string[]): number {
    // Exacte regio match = hoogste score
    if (news.targetRegions.some(r => userRegions.includes(r))) return 100;
    // Nationaal = medium score
    if (news.targetScope === 'nationaal') return 50;
    // Internationaal = basis score
    if (news.targetScope === 'internationaal') return 25;
    return 0;
  }

  /**
   * Tel nieuws per filter tab
   */
  getNewsCountsByTab(
    news: NetworkClubNews[],
    userRegions: string[]
  ): Record<NewsFilterTab, number> {
    return {
      all: news.length,
      international: news.filter(n => n.targetScope === 'internationaal').length,
      national: news.filter(n => n.targetScope === 'nationaal').length,
      myRegion: news.filter(n => 
        (n.targetScope === 'lokaal' || n.targetScope === 'regionaal') &&
        n.targetRegions.some(r => userRegions.includes(r))
      ).length
    };
  }
}

// Export singleton instance
export const networkClubNewsService = new NetworkClubNewsService();
export default networkClubNewsService;
